import React from "react";
import { View, StyleSheet } from "react-native";
import RHeading from "../../../../RHeading/RHeading";
import RText from "../../../../RText";
import Icon from "../../../../Icon";
import { formatRaceTime } from "../../../../../utils/TimeCalculator";
import {
  RacecardRes,
  ResultRes,
} from "../../../../../types/races/racecardAndResultWidget";

/**
 * RacecardAndResultWidgetHeader is a shared component used across
 * the racecard and result widgets
 *
 * It displays the race time, track name, and race title.
 *
 * Depending on if the component is a racecard or a result widget,
 * it shows a different icon on the right side (chevron for racecard,
 * play button for result).
 *
 * @property {boolean} isRacecard - Indicates if the widget is a racecard (default: false),
 * otherwise it's a result widget
 * @property {object} data - Object containing race information (start time, track name, title)
 *
 * @returns {JSX.Element} The RacecardAndResultWidgetHeader component.
 */

type Props = {
  isRacecard: boolean;
  data: RacecardRes | ResultRes;
};

const RacecardAndResultWidgetHeader = ({ isRacecard, data }: Props) => {
  const raceTime = formatRaceTime(data?.start_time_scheduled);
  const trackName = data?.meeting?.track?.name ?? "";
  const raceTitle = data?.title?.capitalize() ?? "";

  return (
    <View style={styles.container}>
      <View style={styles.flex}>
        <RHeading
          level="4"
          boldText
          color="secondary"
          uppercase
          sizeOverride="md"
        >
          {[raceTime, trackName].filter(Boolean).join(" ")}
        </RHeading>
        <RText numberOfLines={1} size="xs">{raceTitle}</RText>
      </View>
      <View style={styles.iconContainer}>
        <Icon
          name={isRacecard ? "chevronPinkLeft" : "playCircled"}
          rotate={isRacecard ? 180 : 0}
          size={isRacecard ? "small" : "medium"}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  flex: { flex: 1 },
  iconContainer: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default RacecardAndResultWidgetHeader;
